/**
 * Three.js camera for the tilted-overhead projection — reproduces the client's
 * 2D projection (`../projection.ts`) so a 3D character stands exactly where the
 * 2D renderer places the same sim entity (#237, first slice of #231).
 *
 * LOAD-BEARING ALIGNMENT. The one hard requirement of this module: for a sim
 * ground point (x, y), projecting the corresponding 3D world point through this
 * camera must land on the SAME screen pixel as `worldToScreen(x, y, center)`.
 * Everything here is DERIVED from the projection constants — no hand-invented
 * angles — so the two renderers can never drift apart.
 *
 * ── Derivation (why these numbers) ───────────────────────────────────────────
 * The 2D forward transform is (#730, axis-aligned basis)
 *     sx = x * ISO_SCALE_X * zoom + cx
 *     sy = y * ISO_SCALE_Y * zoom + cy          (screen y grows downward)
 * with k = ISO_SCALE_Y / ISO_SCALE_X the depth compression (0.75, #432).
 *
 * Map sim (x, y, height) to a right-handed Y-up world: worldX = x, worldY =
 * height, worldZ = y (`simToWorld3`). Require an orthographic camera whose
 * screen-right axis R is horizontal (level horizon). Matching the transform
 * above then forces, with ZERO free parameters:
 *
 *   • YAW = 0. Screen-right maps to world +x directly — R = (1, 0, 0). (It was
 *     45° while the 2D basis was the rotated `(x−y, x+y)` diamond; #730
 *     axis-aligned the basis, so the yaw goes with it.)
 *   • ELEVATION = asin(ISO_SCALE_Y / ISO_SCALE_X) = asin(k) ≈ 48.6°.
 *       This is the camera pitch below horizontal: a world-z (sim y) unit
 *       displacement moves the screen down by sin(ELEVATION) of the per-unit
 *       scale, which must equal ISO_SCALE_Y ⇒ sin(ELEVATION) = k. NOTE: the
 *       on-SCREEN slope of a ground line and the 3D pitch are different angles
 *       (asin vs atan); conflating them is the classic trap.
 *   • PIXELS-PER-WORLD-UNIT s = ISO_SCALE_X / cos(YAW) = ISO_SCALE_X, times the
 *       viewport `zoom` carried by the `ProjectionCenter` (#730).
 *
 * The camera looks at the ground point currently under the screen centre
 * (the inverse of `worldToScreen` for the given `center`), so a follow camera's
 * edge-clamped `center` is reproduced exactly.
 *
 * PRESENTATION ONLY. Nothing here touches the sim or any hash. Floats are legal.
 */

import * as THREE from "three";
import {
  FACING_SECTORS,
  ISO_SCALE_X,
  ISO_SCALE_Y,
  type ProjectionCenter,
  type ScreenPos,
} from "../projection.js";

/** Screen-right maps to world +x directly (#730): zero yaw about world-up. */
export const ISO_YAW = 0;

/**
 * Camera pitch below horizontal. Derived from the depth-compression ratio baked
 * into the projection scales — `asin(0.75) ≈ 48.6°` for the shipped constants.
 * (See the module header on why this is asin, not atan.)
 */
export const ISO_ELEVATION = Math.asin(ISO_SCALE_Y / ISO_SCALE_X);

/** Screen pixels per world unit at viewport zoom 1. `ISO_SCALE_X / cos(YAW)`
 *  = `ISO_SCALE_X` now that the yaw is zero (#730). */
export const ISO_PX_PER_UNIT = ISO_SCALE_X / Math.cos(ISO_YAW);

/**
 * Unit vector from the look-at target toward the camera (world space). Placing
 * the camera at `target + BACK · distance` and looking back at the target
 * yields screen-right R = (1, 0, 0) and the derived screen-up (0, cos EL,
 * −sin EL), matching the projection. Distance is irrelevant to an orthographic
 * projection (it only shifts the depth range), so any positive multiple works.
 */
export const CAMERA_BACK = new THREE.Vector3(
  Math.sin(ISO_YAW) * Math.cos(ISO_ELEVATION),
  Math.sin(ISO_ELEVATION),
  Math.cos(ISO_YAW) * Math.cos(ISO_ELEVATION),
);

/** How far back to place the camera along `CAMERA_BACK`, in sim units. Large
 *  enough that the whole arena + character heights sit inside [near, far]. */
const CAMERA_DISTANCE = 20000;

/**
 * Map a sim ground coordinate (+ optional height above the floor) to a
 * right-handed, Y-up Three world position: worldX = x, worldY = height,
 * worldZ = y. This is the ONLY sim→3D coordinate mapping; keep it in one place.
 */
export function simToWorld3(x: number, y: number, height = 0): THREE.Vector3 {
  return new THREE.Vector3(x, height, y);
}

/**
 * Target Y rotation (radians) for a sim facing sector. Sector 0 = North; the
 * sim numbers sectors clockwise. Cosmetic only.
 *
 * Lives here, next to `simToWorld3`, because it is the OTHER half of the
 * sim→3D mapping (position and orientation), and because more than one
 * consumer must agree on it: `threeLayer.ts` lerps a character's yaw toward
 * this value, and the #825 ground marker takes that same yaw so the marker's
 * facing wedge and the model's forward axis can never disagree.
 */
export function facingToYaw(facing: number): number {
  const sector = ((Math.trunc(facing) % FACING_SECTORS) + FACING_SECTORS) % FACING_SECTORS;
  const theta = (sector * (2 * Math.PI)) / FACING_SECTORS; // clockwise from North
  // Model forward is +Z at yaw 0; face world dir (sinθ, 0, -cosθ) ⇒ yaw = π - θ.
  return Math.PI - theta;
}

/**
 * Solve for the sim ground point that sits under the screen centre for a given
 * projection `center`, by inverting `worldToScreen` at (W/2, H/2). Un-rounded
 * (unlike `screenToWorld`, which snaps to integer sim units) so the camera aims
 * precisely. Pure.
 */
function groundUnderScreenCentre(
  viewportW: number,
  viewportH: number,
  center: ProjectionCenter,
): { readonly tx: number; readonly ty: number } {
  // worldToScreen(tx,ty,center) = (W/2, H/2) ⇒ (#730: the axis-aligned basis
  // inverts per-axis; the viewport zoom scales both)
  //   tx = (W/2 - cx) / (ISO_SCALE_X · zoom)
  //   ty = (H/2 - cy) / (ISO_SCALE_Y · zoom)
  return {
    tx: (viewportW / 2 - center.cx) / (ISO_SCALE_X * center.zoom),
    ty: (viewportH / 2 - center.cy) / (ISO_SCALE_Y * center.zoom),
  };
}

/**
 * Create the orthographic iso camera. The frustum + position are set by
 * `updateIsoCamera` (needs the live viewport + camera centre), so a freshly
 * created camera must be updated once before it projects correctly.
 */
export function createIsoCamera(): THREE.OrthographicCamera {
  const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, CAMERA_DISTANCE * 2);
  cam.up.set(0, 1, 0);
  return cam;
}

/**
 * Configure the camera for the current viewport (CSS px, matching the values
 * fed to `worldToScreen`) and projection `center`. Sets a symmetric ortho
 * frustum scaled to `ISO_PX_PER_UNIT`, aims the camera at the ground point
 * under the screen centre, and refreshes the matrices so projection is
 * immediately valid.
 */
export function updateIsoCamera(
  cam: THREE.OrthographicCamera,
  viewportW: number,
  viewportH: number,
  center: ProjectionCenter,
): void {
  // World units spanned by the viewport at s px/unit (s scales with the
  // viewport cover zoom, #730); symmetric about the axis.
  const pxPerUnit = ISO_PX_PER_UNIT * center.zoom;
  const halfW = viewportW / (2 * pxPerUnit);
  const halfH = viewportH / (2 * pxPerUnit);
  cam.left = -halfW;
  cam.right = halfW;
  cam.top = halfH;
  cam.bottom = -halfH;
  cam.near = 1;
  cam.far = CAMERA_DISTANCE * 2;

  const { tx, ty } = groundUnderScreenCentre(viewportW, viewportH, center);
  const target = simToWorld3(tx, ty, 0);
  cam.position.copy(target).addScaledVector(CAMERA_BACK, CAMERA_DISTANCE);
  cam.up.set(0, 1, 0);
  cam.lookAt(target);
  cam.updateMatrixWorld(true);
  cam.updateProjectionMatrix();
}

/**
 * Project a sim ground point (x, y) through the camera to screen-space pixels,
 * mirroring `worldToScreen`. Used by the alignment test (and available to the
 * layer for debugging). The camera must have been `updateIsoCamera`'d for the
 * same viewport.
 */
export function projectSimToScreen(
  cam: THREE.OrthographicCamera,
  x: number,
  y: number,
  viewportW: number,
  viewportH: number,
): ScreenPos {
  const ndc = simToWorld3(x, y, 0).project(cam);
  return {
    sx: ((ndc.x + 1) / 2) * viewportW,
    sy: ((1 - ndc.y) / 2) * viewportH,
  };
}
